/**
 * Asset browser — free 3D models from the Poly Haven open API (CC0, no key).
 * Search/browse thumbnails; clicking one downloads its glTF, flattens the
 * geometry, and drops it into the timeline as a non-parametric import-mesh
 * feature (stored content-addressed in IndexedDB, so re-adds are instant).
 */

import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

import type { App } from '../app';
import { soupToMeshData } from '@core/io/mesh';

const API = 'https://api.polyhaven.com';

interface PolyAsset {
  id: string;
  name: string;
}

export function mountAssetsPanel(host: HTMLElement, app: App): void {
  const panel = document.createElement('div');
  panel.className = 'nv-side';
  panel.style.display = 'none';
  host.appendChild(panel);

  let assets: PolyAsset[] = [];
  let loadState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  let errorMsg = '';
  let query = '';
  let importing = '';

  async function fetchAssets(): Promise<void> {
    loadState = 'loading';
    render();
    try {
      const res = await fetch(`${API}/assets?t=models`);
      if (!res.ok) throw new Error(`Poly Haven API: ${res.status}`);
      const json = (await res.json()) as Record<string, { name: string }>;
      assets = Object.entries(json).map(([id, a]) => ({ id, name: a.name }));
      loadState = 'ready';
    } catch (err) {
      loadState = 'error';
      errorMsg = err instanceof Error ? err.message : String(err);
    }
    render();
  }

  async function importAsset(asset: PolyAsset): Promise<void> {
    importing = asset.id;
    render();
    try {
      const res = await fetch(`${API}/files/${asset.id}`);
      if (!res.ok) throw new Error(`files API: ${res.status}`);
      const files = (await res.json()) as {
        gltf?: Record<string, { gltf?: { url: string } }>;
      };
      const resolutions = files.gltf ?? {};
      const pick = resolutions['1k'] ?? Object.values(resolutions)[0];
      const url = pick?.gltf?.url;
      if (!url) throw new Error('No glTF available for this asset');

      const mesh = await flattenGLTF(url);
      // Poly Haven models are Y-up meters; NovaCAD is Z-up millimeters.
      const { bakeTransform } = await import('@core/io/mesh');
      const converted = bakeTransform(mesh, {
        position: [0, 0, 0],
        rotationEuler: [Math.PI / 2, 0, 0],
        scale: [1000, 1000, 1000],
      });
      await app.addImportedMesh(asset.name, `polyhaven:${asset.id}`, converted);
      importing = '';
      app.togglePanel('assets'); // close and show the import
    } catch (err) {
      importing = '';
      errorMsg = `Import failed: ${err instanceof Error ? err.message : err}`;
      render();
    }
  }

  const render = (): void => {
    panel.style.display = app.activePanel === 'assets' ? 'block' : 'none';
    if (app.activePanel !== 'assets') return;
    if (loadState === 'idle') void fetchAssets();
    panel.innerHTML = '<h3>Asset Library · Poly Haven (CC0)</h3>';

    const search = document.createElement('input');
    search.type = 'text';
    search.placeholder = 'Search models…';
    search.value = query;
    search.className = 'side-search';
    search.addEventListener('input', () => {
      query = search.value;
      render();
      search.focus();
    });
    panel.appendChild(search);

    if (loadState === 'loading') {
      panel.insertAdjacentHTML('beforeend', '<div class="side-status">Loading catalog…</div>');
      return;
    }
    if (loadState === 'error') {
      panel.insertAdjacentHTML(
        'beforeend',
        `<div class="side-status">Could not reach Poly Haven: ${errorMsg}</div>`,
      );
      return;
    }
    if (errorMsg) {
      panel.insertAdjacentHTML('beforeend', `<div class="side-status">${errorMsg}</div>`);
    }

    const grid = document.createElement('div');
    grid.className = 'side-grid';
    const q = query.toLowerCase();
    const shown = assets.filter((a) => a.name.toLowerCase().includes(q)).slice(0, 30);
    for (const asset of shown) {
      const card = document.createElement('div');
      card.className = 'side-card';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = `https://cdn.polyhaven.com/asset_img/thumbs/${asset.id}.png?width=192`;
      const label = document.createElement('span');
      label.textContent = importing === asset.id ? 'Importing…' : asset.name;
      card.append(img, label);
      card.addEventListener('click', () => {
        if (!importing) void importAsset(asset);
      });
      grid.appendChild(card);
    }
    panel.appendChild(grid);
  };

  app.onUI(render);
  render();
}

/** 1×1 transparent PNG — stands in for textures we don't need. */
const NULL_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

/** Load a glTF and flatten every mesh into one world-space triangle soup. */
async function flattenGLTF(url: string): Promise<ReturnType<typeof soupToMeshData>> {
  // Only geometry matters here — swap texture requests for a placeholder so
  // relative texture paths (which don't resolve off the CDN) don't error.
  const manager = new THREE.LoadingManager();
  manager.setURLModifier((u) => (/\.(jpe?g|png|webp|ktx2)(\?|$)/i.test(u) ? NULL_PNG : u));
  const gltf = await new GLTFLoader(manager).loadAsync(url);
  gltf.scene.updateMatrixWorld(true);
  const coords: number[] = [];
  gltf.scene.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    const geo = node.geometry as THREE.BufferGeometry;
    const pos = geo.getAttribute('position');
    if (!pos) return;
    const index = geo.getIndex();
    const v = new THREE.Vector3();
    const count = index ? index.count : pos.count;
    for (let i = 0; i < count; i++) {
      v.fromBufferAttribute(pos, index ? index.getX(i) : i);
      v.applyMatrix4(node.matrixWorld);
      coords.push(v.x, v.y, v.z);
    }
  });
  if (coords.length === 0) throw new Error('glTF contains no mesh geometry');
  return soupToMeshData(new Float32Array(coords));
}
